import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, requestJson } from './client';
import { clearSessionToken } from './embeddedToken';

export interface RoleGrant {
  role: 'hr' | 'admin' | 'auditor';
  organizationId: string;
  departmentId: string | null;
}

export interface CurrentUser {
  userId: string;
  employeeId: string | null;
  organizationId: string;
  displayName: string;
  roles: RoleGrant[];
  /** Есть подчинённые: в меню страницы по команде. */
  hasTeam?: boolean;
}

export const ME_QUERY_KEY = ['me'] as const;

/** Текущий пользователь; null — вход не выполнен. */
export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  try {
    return await requestJson<CurrentUser>('/api/v1/me');
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function useCurrentUser() {
  return useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchCurrentUser, staleTime: 60_000 });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => requestJson<void>('/api/v1/auth/logout', { method: 'POST' }),
    onSettled: () => {
      clearSessionToken();
      queryClient.clear();
      queryClient.setQueryData(ME_QUERY_KEY, null);
    },
  });
}

export const ROLE_LABEL: Record<RoleGrant['role'], string> = {
  hr: 'HR',
  admin: 'Администратор',
  auditor: 'Аудитор',
};

export interface AuthMethods {
  bitrix24: { portal: string; oauth: boolean };
}

/** Способы входа. Ошибка — вход через Bitrix24 на этом сервере не настроен. */
export function useAuthMethods() {
  return useQuery({
    queryKey: ['auth', 'methods'],
    queryFn: () => requestJson<AuthMethods>('/api/v1/auth/methods'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
